import { useState, useEffect } from 'react'
import { X, Save } from 'lucide-react'

interface Goal {
  id: string
  scored_by_team: 'red' | 'blue'
  goal_type: string
  shooter_role: 'attack' | 'midfield' | 'defense' | 'goalkeeper'
  points_awarded: number
  touched_by_defender: boolean
  goal_time: string | null
  created_at: string
  notes?: string
  user: {
    id: string
    display_name: string
    profile_picture_url?: string
  } | null
}

interface EditGoalModalProps {
  goal: Goal | null
  isOpen: boolean
  onClose: () => void
  onSave: (goal: Goal) => void
}

const GOAL_TYPES = [
  { value: 'attack_shot', label: 'Attack Shot' },
  { value: 'midfield_shot', label: 'Midfield Shot' },
  { value: 'defense_shot', label: 'Defense Shot' },
  { value: 'goalkeeper_shot', label: 'Goalkeeper Shot' },
  { value: 'own_goal_attack', label: 'Own Goal (Attack)' },
  { value: 'own_goal_midfield', label: 'Own Goal (Midfield)' },
  { value: 'own_goal_defense', label: 'Own Goal (Defense)' },
  { value: 'own_goal_goalkeeper', label: 'Own Goal (Goalkeeper)' },
  { value: 'gamelle', label: 'Gamelle' },
  { value: 'gamelle_attack', label: 'Gamelle (Attack)' },
  { value: 'gamelle_midfield', label: 'Gamelle (Midfield)' },
  { value: 'gamelle_defense', label: 'Gamelle (Defense)' },
  { value: 'gamelle_goalkeeper', label: 'Gamelle (Goalkeeper)' },
  { value: 'monsterblock_defense', label: 'Monsterblock (Defense)' },
  { value: 'monsterblock_goalkeeper', label: 'Monsterblock (Goalkeeper)' }
]

const SHOOTER_ROLES = [
  { value: 'attack', label: 'Attack' },
  { value: 'midfield', label: 'Midfield' },
  { value: 'defense', label: 'Defense' },
  { value: 'goalkeeper', label: 'Goalkeeper' }
]

export function EditGoalModal({ goal, isOpen, onClose, onSave }: EditGoalModalProps) {
  const [formData, setFormData] = useState({
    scored_by_team: 'red' as 'red' | 'blue',
    goal_type: 'attack_shot',
    shooter_role: 'attack' as 'attack' | 'midfield' | 'defense' | 'goalkeeper',
    touched_by_defender: false,
    goal_time: '',
    notes: ''
  })

  useEffect(() => {
    if (goal && isOpen) {
      setFormData({
        scored_by_team: goal.scored_by_team,
        goal_type: goal.goal_type,
        shooter_role: goal.shooter_role,
        touched_by_defender: goal.touched_by_defender,
        goal_time: goal.goal_time ? new Date(goal.goal_time).toISOString().slice(0, 16) : '',
        notes: goal.notes || ''
      })
    } else if (isOpen) {
      // Reset form for new goal
      setFormData({
        scored_by_team: 'red',
        goal_type: 'attack_shot',
        shooter_role: 'attack',
        touched_by_defender: false,
        goal_time: new Date().toISOString().slice(0, 16),
        notes: ''
      })
    }
  }, [goal, isOpen])

  const handleSave = () => {
    if (!goal) return

    const updatedGoal: Goal = {
      ...goal,
      ...formData,
      goal_time: formData.goal_time ? new Date(formData.goal_time).toISOString() : null,
      points_awarded: calculatePoints(formData.goal_type, formData.scored_by_team, formData.shooter_role, formData.touched_by_defender)
    }

    onSave(updatedGoal)
  }

  const calculatePoints = (goalType: string, scoredByTeam: 'red' | 'blue', shooterRole: string, touchedByDefender: boolean) => {
    // This is a simplified calculation - in a real app, you'd use the same logic as the backend
    const basePoints: Record<string, number> = {
      'attack_shot': 2,
      'midfield_shot': -1,
      'defense_shot': 3,
      'goalkeeper_shot': 5,
      'own_goal_attack': 2,
      'own_goal_midfield': 0,
      'own_goal_defense': 0,
      'own_goal_goalkeeper': 0,
      'gamelle': -2,
      'gamelle_attack': -2,
      'gamelle_midfield': -1,
      'gamelle_defense': -3,
      'gamelle_goalkeeper': -5,
      'monsterblock_defense': 3,
      'monsterblock_goalkeeper': 5
    }

    const touchedPoints: Record<string, number> = {
      'attack_shot': 1,
      'midfield_shot': 1,
      'defense_shot': 1,
      'goalkeeper_shot': 1,
      'own_goal_attack': 1,
      'own_goal_midfield': 1,
      'own_goal_defense': 1,
      'own_goal_goalkeeper': 1,
      'gamelle': -1,
      'gamelle_attack': -1,
      'gamelle_midfield': -1,
      'gamelle_defense': -1,
      'gamelle_goalkeeper': -1,
      'monsterblock_defense': 1,
      'monsterblock_goalkeeper': 1
    }

    return touchedByDefender ? touchedPoints[goalType] || 0 : basePoints[goalType] || 0
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {goal ? 'Edit Goal' : 'Add Goal'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Team */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Team
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="red"
                  checked={formData.scored_by_team === 'red'}
                  onChange={(e) => setFormData({ ...formData, scored_by_team: e.target.value as 'red' | 'blue' })}
                  className="mr-2"
                />
                <span className="text-red-600 font-medium">Red Team</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="blue"
                  checked={formData.scored_by_team === 'blue'}
                  onChange={(e) => setFormData({ ...formData, scored_by_team: e.target.value as 'red' | 'blue' })}
                  className="mr-2"
                />
                <span className="text-blue-600 font-medium">Blue Team</span>
              </label>
            </div>
          </div>

          {/* Goal Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Goal Type
            </label>
            <select
              value={formData.goal_type}
              onChange={(e) => setFormData({ ...formData, goal_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {GOAL_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Shooter Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shooter Position
            </label>
            <select
              value={formData.shooter_role}
              onChange={(e) => setFormData({ ...formData, shooter_role: e.target.value as 'attack' | 'midfield' | 'defense' | 'goalkeeper' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SHOOTER_ROLES.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Touched by Defender */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.touched_by_defender}
                onChange={(e) => setFormData({ ...formData, touched_by_defender: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">
                Touched by defender
              </span>
            </label>
          </div>

          {/* Goal Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Goal Time
            </label>
            <input
              type="datetime-local"
              value={formData.goal_time}
              onChange={(e) => setFormData({ ...formData, goal_time: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any notes about this goal..."
            />
          </div>

          {/* Points Preview */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Points awarded:</strong> {calculatePoints(formData.goal_type, formData.scored_by_team, formData.shooter_role, formData.touched_by_defender)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Save className="h-4 w-4 mr-2" />
            {goal ? 'Update Goal' : 'Add Goal'}
          </button>
        </div>
      </div>
    </div>
  )
}
